import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Table, Form, Container, Alert, Modal, Button, Card, InputGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { AiOutlineClear, AiOutlineInfoCircle, AiOutlinePlus, AiOutlineClose, AiOutlineCheck, AiOutlineMinus, AiOutlineSearch } from "react-icons/ai";

const FIELDS = [
    { key: "taxesZus", labelKey: "zus" },
    { key: "partner", labelKey: "partner" },
    { key: "accountant", labelKey: "accountant" },
    { key: "sallary", labelKey: "sallary" },
    { key: "carRent", labelKey: "carRent" },
];

const normalizeAmount = (v) => (v || "").toString().trim().replace(",", ".");

const OtherCell = ({ driverId, getOtherData, removeOtherExpense, removeRefundItem, openOtherModal, t }) => {
    const { otherExpenses, refundsItems } = getOtherData(driverId);
    return (
        <div className="small d-flex gap-2 h-100" style={{ alignItems: "stretch" }}>
            <div className="flex-grow-1 overflow-auto min-w-0" style={{ minHeight: 0 }}>
                {otherExpenses.length > 0 && (
                    <div className="d-flex flex-column gap-1 mb-1">
                        {otherExpenses.map((e, i) => (
                            <Card key={`e-${i}`} className="border-0 shadow-sm flex-shrink-0" style={{ borderLeft: "3px solid var(--bs-danger)", backgroundColor: "rgba(220, 53, 69, 0.08)" }}>
                                <Card.Body className="py-1 px-2 d-flex align-items-center gap-2">
                                    <AiOutlineMinus className="flex-shrink-0 text-danger" style={{ fontSize: "1rem" }} title={t("otherCosts")} />
                                    <span className="text-break flex-grow-1">{e.amount}{e.description ? `: ${e.description}` : ""}</span>
                                    <AiOutlineClose
                                        className="flex-shrink-0 text-danger"
                                        style={{ cursor: "pointer", fontSize: "0.9rem" }}
                                        title={t("remove")}
                                        onClick={(ev) => { ev.stopPropagation(); removeOtherExpense(driverId, i); }}
                                    />
                                </Card.Body>
                            </Card>
                        ))}
                    </div>
                )}
                {refundsItems.length > 0 && (
                    <div className="d-flex flex-column gap-1 mb-1">
                        {refundsItems.map((r, i) => (
                            <Card key={`r-${i}`} className="border-0 shadow-sm flex-shrink-0" style={{ borderLeft: "3px solid var(--bs-success)", backgroundColor: "rgba(25, 135, 84, 0.08)" }}>
                                <Card.Body className="py-1 px-2 d-flex align-items-center gap-2">
                                    <AiOutlinePlus className="flex-shrink-0 text-success" style={{ fontSize: "1rem" }} title={t("refunds")} />
                                    <span className="text-break flex-grow-1">{r.amount}{r.description ? `: ${r.description}` : ""}</span>
                                    <AiOutlineClose
                                        className="flex-shrink-0 text-danger"
                                        style={{ cursor: "pointer", fontSize: "0.9rem" }}
                                        title={t("remove")}
                                        onClick={(ev) => { ev.stopPropagation(); removeRefundItem(driverId, i); }}
                                    />
                                </Card.Body>
                            </Card>
                        ))}
                    </div>
                )}
            </div>
            <div className="d-flex align-items-center justify-content-center flex-shrink-0">
                <AiOutlinePlus
                    className="text-secondary"
                    style={{ cursor: "pointer", fontSize: "1rem" }}
                    title={t("add")}
                    onClick={(e) => { e.stopPropagation(); openOtherModal(driverId, "expense"); }}
                />
            </div>
        </div>
    );
};

const getAuthConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

/** Заголовок таблиці лишається видимим при прокрутці сторінки (як .table-light) */
const stickyHeaderCellStyle = {
    position: "sticky",
    top: 0,
    zIndex: 2,
    backgroundColor: "#f8f9fa",
    boxShadow: "inset 0 -1px 0 rgba(0, 0, 0, 0.09)",
};

const ExtraFundsDefaultValues = () => {
    const { t } = useTranslation();
    const [drivers, setDrivers] = useState([]);
    const [defaults, setDefaults] = useState({});
    const [editCell, setEditCell] = useState({ driverId: null, field: null });
    const [editedValue, setEditedValue] = useState("");
    const [otherModal, setOtherModal] = useState({ show: false, driverId: null, type: "expense", amount: "", description: "" });
    const [loading, setLoading] = useState(true);
    const [saveError, setSaveError] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await axios.get("/api/extra-funds-default-values", getAuthConfig());
                const list = response.data || [];
                setDrivers(list.map((r) => ({ id: r.driver_id, firstName: r.firstName, lastName: r.lastName })));
                const byDriver = {};
                list.forEach((r) => {
                    byDriver[r.driver_id] = {
                        taxesZus: r.taxesZus != null ? String(r.taxesZus) : "",
                        partner: r.partner != null ? String(r.partner) : "",
                        accountant: r.accountant != null ? String(r.accountant) : "",
                        sallary: r.sallary != null ? String(r.sallary) : "",
                        carRent: r.carRent != null ? String(r.carRent) : "",
                        otherExpenses: Array.isArray(r.otherExpenses) ? r.otherExpenses : [],
                        refundsItems: Array.isArray(r.refundsItems) ? r.refundsItems : [],
                    };
                });
                setDefaults(byDriver);
            } catch (error) {
                console.error("Error fetching extra-funds-default-values", error);
                setSaveError(error.response?.data?.error || error.message);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const filteredDrivers = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return drivers;
        const qCompact = q.replace(/\s+/g, "");
        return drivers.filter((d) => {
            const first = (d.firstName || "").toLowerCase();
            const last = (d.lastName || "").toLowerCase();
            const full = `${first} ${last}`.trim();
            const fullCompact = full.replace(/\s+/g, "");
            return first.includes(q) || last.includes(q) || full.includes(q) || (qCompact && fullCompact.includes(qCompact));
        });
    }, [drivers, searchQuery]);

    const getCellValue = (driverId, field) => {
        const d = defaults[driverId];
        if (!d || d[field] === undefined || d[field] === null) return "";
        return String(d[field]);
    };

    const getOtherData = (driverId) => {
        const d = defaults[driverId];
        const otherExpenses = Array.isArray(d?.otherExpenses) ? d.otherExpenses : [];
        const refundsItems = Array.isArray(d?.refundsItems) ? d.refundsItems : [];
        return {
            otherExpenses: otherExpenses.filter((e) => (e?.amount ?? "").toString().trim() !== "" || (e?.description ?? "").toString().trim() !== ""),
            refundsItems: refundsItems.filter((r) => (r?.amount ?? "").toString().trim() !== "" || (r?.description ?? "").toString().trim() !== ""),
        };
    };

    const saveDefaultsToApi = async (driverId, rowData) => {
        const row = rowData || defaults[driverId] || {};
        const payload = {
            taxesZus: row.taxesZus ?? "",
            partner: row.partner ?? "",
            accountant: row.accountant ?? "",
            carRent: row.carRent ?? "",
            sallary: row.sallary ?? "",
            otherExpenses: Array.isArray(row.otherExpenses) ? row.otherExpenses : [],
            refundsItems: Array.isArray(row.refundsItems) ? row.refundsItems : [],
        };
        try {
            setSaveError(null);
            await axios.put(`/api/extra-funds-default-values/${driverId}`, payload, getAuthConfig());
        } catch (err) {
            console.error("Save default values failed", err);
            setSaveError(err.response?.data?.error || err.message);
        }
    };

    const setOtherData = (driverId, { otherExpenses, refundsItems }) => {
        const next = {
            ...defaults,
            [driverId]: {
                ...(defaults[driverId] || {}),
                otherExpenses: otherExpenses || [],
                refundsItems: refundsItems || [],
            },
        };
        setDefaults(next);
        saveDefaultsToApi(driverId, next[driverId]);
    };

    const openOtherModal = (driverId, type = "expense") => {
        setOtherModal({ show: true, driverId, type, amount: "", description: "" });
    };

    const closeOtherModal = () => {
        setOtherModal({ show: false, driverId: null, type: "expense", amount: "", description: "" });
    };

    const handleOtherModalAmountChange = (e) => {
        const value = e.target.value;
        let filtered = value.replace(/[^\d.,]/g, "");
        const parts = filtered.split(/[.,]/);
        if (parts.length > 2) filtered = parts[0] + "." + parts.slice(1).join("");
        setOtherModal((prev) => ({ ...prev, amount: filtered }));
    };

    const handleAddOtherEntry = () => {
        const { driverId, type, amount, description } = otherModal;
        if (!driverId) return;
        const amountNorm = normalizeAmount(amount);
        const desc = (description || "").toString().trim();
        if (amountNorm === "" && desc === "") return;

        const { otherExpenses, refundsItems } = getOtherData(driverId);
        const item = { amount: amountNorm || "0", description: desc };

        if (type === "expense") {
            setOtherData(driverId, { otherExpenses: [...otherExpenses, item], refundsItems });
        } else {
            setOtherData(driverId, { otherExpenses, refundsItems: [...refundsItems, item] });
        }
        closeOtherModal();
    };

    const removeOtherExpense = (driverId, index) => {
        const { otherExpenses, refundsItems } = getOtherData(driverId);
        setOtherData(driverId, {
            otherExpenses: otherExpenses.filter((_, i) => i !== index),
            refundsItems,
        });
    };

    const removeRefundItem = (driverId, index) => {
        const { otherExpenses, refundsItems } = getOtherData(driverId);
        setOtherData(driverId, {
            otherExpenses,
            refundsItems: refundsItems.filter((_, i) => i !== index),
        });
    };

    const getCellBgStyle = (value) => {
        const trimmed = (value || "").toString().trim();
        if (trimmed === "") return { backgroundColor: "#fef2f2" }; // пусто — світло-червоний
        const num = parseFloat(trimmed.replace(",", "."));
        if (Number.isNaN(num) || num === 0) return { backgroundColor: "#fefce8" }; // 0 — жовтий
        return { backgroundColor: "#f0fdf4" }; // заповнено — світло-зелений
    };

    const handleDoubleClick = (driverId, field, currentValue) => {
        setEditCell({ driverId, field });
        setEditedValue(currentValue);
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        // Дозволяємо лише цифри та один десятковий роздільник (крапка або кома)
        let filtered = value.replace(/[^\d.,]/g, "");
        const parts = filtered.split(/[.,]/);
        if (parts.length > 2) {
            filtered = parts[0] + "." + parts.slice(1).join("");
        }
        setEditedValue(filtered);
    };

    const handleBlur = () => {
        if (editCell.driverId == null || editCell.field == null) return;

        const nextDefaults = {
            ...defaults,
            [editCell.driverId]: {
                ...(defaults[editCell.driverId] || {}),
                [editCell.field]: editedValue.trim() === "" ? "" : editedValue.replace(",", "."),
            },
        };
        setDefaults(nextDefaults);
        saveDefaultsToApi(editCell.driverId, nextDefaults[editCell.driverId]);
        setEditCell({ driverId: null, field: null });
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleBlur();
        }
    };

    const handleClearRow = async (driverId) => {
        try {
            setSaveError(null);
            await axios.delete(`/api/extra-funds-default-values/${driverId}`, getAuthConfig());
        } catch (err) {
            console.error("Clear default values failed", err);
            setSaveError(err.response?.data?.error || err.message);
        }
        const next = { ...defaults };
        delete next[driverId];
        setDefaults(next);
        if (editCell.driverId === driverId) {
            setEditCell({ driverId: null, field: null });
        }
    };

    return (
        <Container>
            <h2>{t("extraCosts")} — {t("standardValues")}</h2>

            {saveError && (
                <Alert variant="danger" onClose={() => setSaveError(null)} dismissible className="mt-3">
                    {saveError}
                </Alert>
            )}

            {loading ? (
                <p className="text-muted mt-4">{t("loading") || "Завантаження…"}</p>
            ) : (
            <>
            <Form.Group className="mt-4 mb-3" controlId="extra-funds-defaults-search">
                <InputGroup>
                    <InputGroup.Text aria-hidden>
                        <AiOutlineSearch aria-hidden />
                    </InputGroup.Text>
                    <Form.Control
                        type="search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={t("searchDriverPlaceholder")}
                        autoComplete="off"
                        aria-label={t("searchDriverPlaceholder")}
                    />
                </InputGroup>
            </Form.Group>
            <Table bordered hover className="mt-0" style={{ tableLayout: "fixed" }}>
                <colgroup>
                    <col style={{ width: "25%" }} />
                    {FIELDS.map((_, i) => (
                        <col key={i} style={{ width: "10%" }} />
                    ))}
                    <col style={{ width: "25%" }} />
                </colgroup>
                <thead className="table-light">
                    <tr>
                        <th style={stickyHeaderCellStyle}>{t("driver")}</th>
                        {FIELDS.map(({ labelKey }) => (
                            <th key={labelKey} style={stickyHeaderCellStyle}>{t(labelKey)}</th>
                        ))}
                        <th style={stickyHeaderCellStyle}>{t("other")}</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredDrivers.length === 0 ? (
                        <tr>
                            <td colSpan={2 + FIELDS.length} className="text-muted text-center py-4">
                                {drivers.length === 0 ? t("noDrivers") : t("nothingFoundForSearch")}
                            </td>
                        </tr>
                    ) : (
                    filteredDrivers.map((driver) => (
                        <tr key={driver.id}>
                            <td className="text-nowrap overflow-hidden" title={`${driver.firstName} ${driver.lastName}`}>
                                <span className="d-flex align-items-center gap-1">
                                    <span className="overflow-hidden text-truncate flex-grow-1">
                                        {driver.firstName} {driver.lastName}
                                    </span>
                                    <AiOutlineClear
                                        className="flex-shrink-0 text-secondary"
                                        style={{ cursor: "pointer", fontSize: "1.1rem" }}
                                        title={t("clearRow")}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleClearRow(driver.id);
                                        }}
                                    />
                                </span>
                            </td>
                            {FIELDS.map(({ key }) => {
                                const isEditing =
                                    editCell.driverId === driver.id && editCell.field === key;
                                const value = getCellValue(driver.id, key);

                                return (
                                    <td
                                        key={key}
                                        onDoubleClick={() =>
                                            handleDoubleClick(driver.id, key, value)
                                        }
                                        style={{ cursor: "text", ...getCellBgStyle(value) }}
                                    >
                                        {isEditing ? (
                                            <Form.Control
                                                type="text"
                                                inputMode="decimal"
                                                value={editedValue}
                                                onChange={handleInputChange}
                                                onBlur={handleBlur}
                                                onKeyDown={handleKeyDown}
                                                autoFocus
                                                size="sm"
                                                className="w-100"
                                                style={{ minWidth: 0, maxWidth: "100%", boxSizing: "border-box" }}
                                            />
                                        ) : (
                                            value || "—"
                                        )}
                                    </td>
                                );
                            })}
                            <td style={{ verticalAlign: "top", backgroundColor: "#fff" }}>
                                <OtherCell
                                    driverId={driver.id}
                                    getOtherData={getOtherData}
                                    removeOtherExpense={removeOtherExpense}
                                    removeRefundItem={removeRefundItem}
                                    openOtherModal={openOtherModal}
                                    t={t}
                                />
                            </td>
                        </tr>
                    ))
                    )}
                </tbody>
            </Table>
            </>
            )}

            <Modal show={otherModal.show} onHide={closeOtherModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t("addEntry")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>{t("entryType")}</Form.Label>
                        <div className="d-flex gap-2">
                            <Button
                                variant={otherModal.type === "expense" ? "danger" : "outline-danger"}
                                style={{ width: "50%" }}
                                onClick={() => setOtherModal((p) => ({ ...p, type: "expense" }))}
                            >
                                {t("otherCosts")}
                            </Button>
                            <Button
                                variant={otherModal.type === "refund" ? "success" : "outline-success"}
                                style={{ width: "50%" }}
                                onClick={() => setOtherModal((p) => ({ ...p, type: "refund" }))}
                            >
                                {t("refunds")}
                            </Button>
                        </div>
                        <p className="small text-muted mt-2 mb-0">
                            {otherModal.type === "expense" ? t("otherExpenseHint") : t("refundHint")}
                        </p>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{t("value")}</Form.Label>
                        <Form.Control
                            type="text"
                            inputMode="decimal"
                            value={otherModal.amount}
                            onChange={handleOtherModalAmountChange}
                            placeholder="0"
                        />
                    </Form.Group>
                    <Form.Group className="mb-0">
                        <Form.Label>{t("description")}</Form.Label>
                        <Form.Control
                            type="text"
                            value={otherModal.description}
                            onChange={(e) => setOtherModal((p) => ({ ...p, description: e.target.value }))}
                            placeholder={t("description")}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="d-flex gap-2 w-100 justify-content-stretch flex-nowrap">
                    <Button variant="secondary" className="flex-grow-1" style={{ flex: "1 1 0", minWidth: 0 }} onClick={closeOtherModal}>
                        <AiOutlineClose className="me-2" />{t("cancel")}
                    </Button>
                    <Button variant="primary" className="flex-grow-1" style={{ flex: "1 1 0", minWidth: 0 }} onClick={handleAddOtherEntry}>
                        <AiOutlineCheck className="me-2" />{t("add")}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Alert variant="info" className="mt-4 mb-0 d-flex align-items-start gap-2">
                <AiOutlineInfoCircle className="flex-shrink-0 mt-1" style={{ fontSize: "1.25rem" }} />
                <div>
                    <p className="mb-1">{t("standardValuesDescription")}</p>
                    <p className="mb-0">{t("standardValuesSavedToServer") || "Зміни зберігаються на сервері."}</p>
                </div>
            </Alert>
        </Container>
    );
};

export default ExtraFundsDefaultValues;
